import { Box } from "@mui/material";
import { DotLegalFullScreenSpinner, DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { getSettings } from "@common/settingsProvider";
import PublicPageContainer from "@common/components/publicPageContainer/PublicPageContainer";
import { usePublicRiskObservationDataMapping } from "./PublicRiskObservation.hooks";
import { publicRiskObservationStyles as styles } from "./PublicRiskObservation.styles";
import { ReportObservationStep, totalReportObservationSteps } from "./PublicRiskObservation.types";
import WelcomeStep from "./steps/WelcomeStep";
import ObservationStep from "./steps/ObservationStep";
import SourceStep from "./steps/SourceStep";
import ReporterStep from "./steps/ReporterStep";
import SuccessStep from "./steps/SuccessStep";

const stepTitleKeys: Record<ReportObservationStep, string> = {
    [ReportObservationStep.Welcome]: "reportObservationStepWelcome",
    [ReportObservationStep.Observation]: "observation",
    [ReportObservationStep.Source]: "source",
    [ReportObservationStep.Reporter]: "reportObservationStepReporter",
    [ReportObservationStep.Success]: "sent",
};

function PublicRiskObservation() {
    const { translateString } = useTranslation();
    const {
        isLoading,
        isLinkActive,
        customerName,
        emailLogoBlobId,
        step,
        stepNumber,
        form,
        validation,
        sourceOptions,
        selectedSourceName,
        hasRememberedReporter,
        isSubmitting,
        setName,
        setDescription,
        setSource,
        setObservedBy,
        setObservedByEmail,
        goToObservationStep,
        goToSourceStep,
        goToReporterStep,
        goBackToSourceStep,
        submit,
        startAnotherReport,
        finish,
    } = usePublicRiskObservationDataMapping();

    if (isLoading) {
        return <DotLegalFullScreenSpinner />;
    }

    if (!isLinkActive) {
        return (
            <PublicPageContainer centerVertically maxWidth={560}>
                <DotLegalHeader headerStyle="medium" fontWeight={400} center marginBottom={0}>
                    {translateString("reportObservationLinkNotFound")}
                </DotLegalHeader>
            </PublicPageContainer>
        );
    }

    const renderStep = () => {
        switch (step) {
            case ReportObservationStep.Welcome:
                return <WelcomeStep onStart={goToObservationStep} />;
            case ReportObservationStep.Observation:
                return (
                    <ObservationStep
                        name={form.name}
                        description={form.description}
                        nameErrorText={validation.getErrorsForDisplay("name")}
                        onNameChange={setName}
                        onDescriptionChange={setDescription}
                        onBack={finish}
                        onContinue={goToSourceStep}
                    />
                );
            case ReportObservationStep.Source:
                return (
                    <SourceStep
                        selectedSource={form.source}
                        sourceOptions={sourceOptions}
                        onSourceChange={setSource}
                        onBack={goToObservationStep}
                        onContinue={goToReporterStep}
                    />
                );
            case ReportObservationStep.Reporter:
                return (
                    <ReporterStep
                        observedBy={form.observedBy}
                        observedByEmail={form.observedByEmail}
                        observedByErrorText={validation.getErrorsForDisplay("observedBy")}
                        observedByEmailErrorText={validation.getErrorsForDisplay("observedByEmail")}
                        hasRememberedReporter={hasRememberedReporter}
                        isSubmitting={isSubmitting}
                        onObservedByChange={setObservedBy}
                        onObservedByEmailChange={setObservedByEmail}
                        onBack={goBackToSourceStep}
                        onSubmit={submit}
                    />
                );
            default:
                return (
                    <SuccessStep
                        name={form.name}
                        sourceName={selectedSourceName}
                        observedBy={form.observedBy}
                        observedByEmail={form.observedByEmail}
                        onSendAnother={startAnotherReport}
                        onDone={finish}
                    />
                );
        }
    };

    return (
        <PublicPageContainer maxWidth={560} fillHeightOnSmallScreen logoWithoutPayoff>
            <Box sx={styles.page}>
                <Box sx={styles.topBar}>
                    <Box sx={[styles.stepChip, step === ReportObservationStep.Success && styles.stepChipSent]}>
                        <span>{translateString(stepTitleKeys[step])}</span>
                        {stepNumber && (
                            <Box component="span" sx={styles.stepCounter}>
                                {stepNumber}/{totalReportObservationSteps}
                            </Box>
                        )}
                    </Box>
                    {emailLogoBlobId ? (
                        <Box component="img" src={`${getSettings().apiBaseUrl}/CustomerAssets/EmailLogo/${emailLogoBlobId}`} alt="" sx={styles.customerLogo} />
                    ) : (
                        customerName && (
                            <DotLegalHeader headerStyle={"medium"} marginBottom={0}>
                                {customerName}
                            </DotLegalHeader>
                        )
                    )}
                </Box>
                {renderStep()}
            </Box>
        </PublicPageContainer>
    );
}

export default PublicRiskObservation;
